import { useCallback, useEffect, useMemo, useRef } from 'react';

import { link, params } from '@aline/routes';
import type { Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { uniqBy } from 'ramda';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ReportRowQuestionJob } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { documentTypename, extractionQuestionTypename, useDocumentParams } from 'src/helpers';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { v4 as uuidv4 } from 'uuid';

import { addCreatedExtractionQuestionToCache } from './report-column-options-cache';
import { createQuestionJobKey } from '../document';

export function useGetExtractionQuestion(
  id: UUID,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetExtractionQuestionQuery>[0]>,
    'variables'
  >
) {
  const { data, loading } = Apollo.useGetExtractionQuestionQuery({ variables: { id }, ...options });
  return { extractionQuestion: data?.extractionQuestion ?? null, loading };
}

export function useGetExtractionQuestionWithInputs(id: UUID) {
  const { data, loading } = Apollo.useGetExtractionQuestionWithInputsQuery({ variables: { id } });
  return { extractionQuestion: data?.extractionQuestion ?? null, loading };
}

export function useGetExtractionQuestions(
  options?: Parameters<typeof Apollo.useGetExtractionQuestionsQuery>[0]
) {
  const { data, loading } = Apollo.useGetExtractionQuestionsQuery(options);
  const extractionQuestions = useMemo(
    () => data?.extractionQuestions ?? [],
    [data?.extractionQuestions]
  );
  return { extractionQuestions, loading };
}

export function useGetReportQuestions(
  options?: Parameters<typeof Apollo.useGetReportQuestionsQuery>[0]
) {
  const { data, loading } = Apollo.useGetReportQuestionsQuery(options);
  const questions = useMemo(() => data?.extractionQuestions ?? [], [data?.extractionQuestions]);
  return { loading, questions };
}

export function useGetExtractionQuestionsForDocument(
  documentId: UUID,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetExtractionQuestionsForDocumentQuery>[0]>,
    'variables'
  >
) {
  const { data, loading } = Apollo.useGetExtractionQuestionsForDocumentQuery({
    ...options,
    variables: { documentId },
  });
  const extractionQuestions = useMemo(
    () => data?.extractionQuestionsForDocument?.extractionQuestions ?? [],
    [data?.extractionQuestionsForDocument?.extractionQuestions]
  );
  return {
    document: data?.extractionQuestionsForDocument?.document ?? null,
    extractionQuestions,
    loading,
  };
}

/** Creates a extractionQuestion template. */
export function useCreateExtractionQuestion() {
  const [createExtractionQuestion, { loading }] = Apollo.useCreateExtractionQuestionMutation();
  const safeCreateExtractionQuestion = useCallback(
    (options: Parameters<Apollo.CreateExtractionQuestionMutationHookResult[0]>[0]) => {
      return createExtractionQuestion({
        ...options,
        update(cache, { data }) {
          if (!data?.createExtractionQuestion) return;
          addCreatedExtractionQuestionToCache(cache, data.createExtractionQuestion);
        },
      });
    },
    [createExtractionQuestion]
  );

  return {
    createExtractionQuestion: safeCreateExtractionQuestion,
    loading,
  };
}

export function useCreateExtractionJobForDocument(documentId: UUID) {
  const [createExtractionJob, { loading }] = Apollo.useCreateExtractionJobMutation();
  const safeCreateExtractionJob = useCallback(
    (options: Parameters<Apollo.CreateExtractionJobMutationHookResult[0]>[0]) => {
      return createExtractionJob({
        ...options,
        update: (cache, { data }) => {
          if (!data?.createExtractionJob?.extractionJob) return;

          /**
           * Needs to satisfy ReportRowQuestionJob so it shows up in DataGrid after creation
           * w/o runtime errors.
           */
          const reportRowJob: ReportRowQuestionJob = {
            ...data.createExtractionJob.extractionJob,
            question: data.createExtractionJob,
          };
          const newExtractionJobRef = cache.writeFragment({
            data: reportRowJob,
            fragment: gql`
              fragment NewExtractionJob on ExtractionJob {
                __typename
                id
              }
            `,
          });
          if (!newExtractionJobRef) return;

          cache.modify({
            fields: {
              [createQuestionJobKey(documentId)]: () => newExtractionJobRef,
            },
            id: cache.identify({
              __typename: extractionQuestionTypename,
              id: options?.variables?.input.questionId,
            }),
          });
          cache.modify({
            fields: {
              questionJobs: (existingJobs = []) => {
                return [...existingJobs, newExtractionJobRef];
              },
            },
            id: cache.identify({
              __typename: documentTypename,
              id: documentId,
            }),
          });
        },
      });
    },
    [createExtractionJob, documentId]
  );

  return {
    createExtractionJob: safeCreateExtractionJob,
    loading,
  };
}

/**
 * Sends a `?questionId=` deep link on to the summary route, where the drawer's AI Properties
 * section is mounted to consume it and scroll to that question.
 *
 * `questionId` is an ephemeral param, so it never reaches `window.location`: TabProvider strips it
 * off the browser URL into the tab's `pendingParams`, and only `useLocation()` inside the tab shows
 * it again. That also means the redirect does not need to carry it — the param rides on the tab,
 * not on the route.
 *
 * Peeks rather than consuming, since AI Properties is the consumer and cannot claim the param until
 * the drawer it lives in is open. The latch keeps a param nobody claims (a question this document
 * doesn't have) from dragging the user back to the summary route on their next navigation, and
 * re-arms once the param is gone so a later deep link to the same question still lands.
 */
export function useOpenDocumentSummaryFromURL() {
  const location = useLocation();
  const navigate = useNavigate();
  const { documentId } = useDocumentParams();
  const { openDrawer } = useGlobalDrawer();
  const redirectedQuestionIdRef = useRef<string | null>(null);
  const questionId = new URLSearchParams(location.search).get(params.questionId);

  useEffect(() => {
    if (!questionId) {
      redirectedQuestionIdRef.current = null;
      return;
    }
    if (redirectedQuestionIdRef.current === questionId) return;

    redirectedQuestionIdRef.current = questionId;
    navigate(link.DocumentDetailSummary({ documentId }), { replace: true });
    openDrawer();
  }, [documentId, navigate, openDrawer, questionId]);
}

/** Deletes an ExtractionQuestion template. */
export function useDeleteExtractionQuestion() {
  const [deleteExtractionQuestion, { loading }] = Apollo.useDeleteExtractionQuestionMutation();
  const safeDeleteExtractionQuestion = useCallback(
    (extractionQuestionId: UUID) => {
      return deleteExtractionQuestion({
        update(cache) {
          const extractionReportColIdKey: keyof Apollo.ExtractionReportColumn = 'questionId';
          const extractionQuestionsKey: keyof Apollo.Query['extractionQuestionsForDocument'] =
            'extractionQuestions';

          cache.modify({
            fields: {
              extractionQuestions: (refs = [], { readField }) => {
                return (refs as Reference[]).filter(
                  (ref: Reference) => readField('id', ref) !== extractionQuestionId
                );
              },
              extractionQuestionsForDocument: (existing, { readField }) => {
                const existingQuestions =
                  readField<Reference[]>(extractionQuestionsKey, existing) ?? [];
                return {
                  ...existing,
                  extractionQuestions: existingQuestions.filter(
                    (ref: Reference) => readField('id', ref) !== extractionQuestionId
                  ),
                };
              },
              reportColumnOptions: (refs = [], { readField }) => {
                return (refs as Reference[]).filter(
                  (ref: Reference) =>
                    readField(extractionReportColIdKey, ref) !== extractionQuestionId
                );
              },
            },
          });
        },
        variables: { extractionQuestionId },
      });
    },
    [deleteExtractionQuestion]
  );

  return {
    deleteExtractionQuestion: safeDeleteExtractionQuestion,
    loading,
  };
}

/**
 * Stops queued and running question runs, selected like a run: document ids,
 * or every document in a report or the repository with `runAllDocs`.
 * `clearRunningJobsOn` drops the cached job of questions known to be running
 * on one document, so the drawer stops showing them before the refetch lands.
 */
export function useCancelExtractionRuns() {
  const { enqueueSnackbar } = useSnackbar();
  const [cancelExtractionRuns, { loading }] = Apollo.useCancelExtractionRunsMutation();
  const safeCancelExtractionRuns = useCallback(
    async (
      input: Apollo.CancelExtractionRunsInput,
      options: {
        clearRunningJobsOn?: { documentId: UUID; questionIds: UUID[] };
        showResult?: boolean;
      } = {}
    ) => {
      const { clearRunningJobsOn, showResult = false } = options;
      const result = await cancelExtractionRuns({
        refetchQueries: [Apollo.namedOperations.Query.GetRunningQuestionIds],
        update: cache => {
          clearRunningJobsOn?.questionIds.forEach(questionId => {
            cache.modify({
              fields: { [createQuestionJobKey(clearRunningJobsOn.documentId)]: () => null },
              id: cache.identify({ __typename: extractionQuestionTypename, id: questionId }),
            });
          });
        },
        variables: { input },
      });
      const stoppedCount = result.data?.cancelExtractionRuns.stoppedCount ?? 0;
      if (showResult)
        enqueueSnackbar(getStoppedRunsMessage(stoppedCount), {
          variant: stoppedCount ? 'success' : 'info',
        });
      return stoppedCount;
    },
    [cancelExtractionRuns, enqueueSnackbar]
  );
  return { cancelExtractionRuns: safeCancelExtractionRuns, loading };
}

export function getStoppedRunsMessage(stoppedCount: number): string {
  if (!stoppedCount) return 'Nothing was running.';
  return `Stopped ${stoppedCount} question ${stoppedCount === 1 ? 'run' : 'runs'}.`;
}

export function useRunExtractionJobsForDocuments() {
  const [startExtractionJobsForDocuments, { loading }] =
    Apollo.useStartExtractionJobsForDocumentsMutation();
  // The run is queued before the mutation returns, so the refetch already sees it.
  const startAndRefetchRunning = useCallback(
    (options: Parameters<Apollo.StartExtractionJobsForDocumentsMutationHookResult[0]>[0]) =>
      startExtractionJobsForDocuments({
        refetchQueries: [Apollo.namedOperations.Query.GetRunningQuestionIds],
        ...options,
      }),
    [startExtractionJobsForDocuments]
  );

  return {
    loading,
    startExtractionJobsForDocuments: startAndRefetchRunning,
  };
}

export function useStartReportExtraction() {
  const { enqueueSnackbar } = useSnackbar();
  const [startReportExtraction, { loading }] = Apollo.useStartReportExtractionMutation();
  const safeStartReportExtraction = useCallback(
    async (options: Parameters<Apollo.StartReportExtractionMutationHookResult[0]>[0]) => {
      try {
        const result = await startReportExtraction({
          refetchQueries: [Apollo.namedOperations.Query.GetRunningQuestionIds],
          ...options,
          update(cache, { data }) {
            const pairs = data?.startReportExtraction?.aiQuestionsRunningForDocs ?? [];
            if (!pairs.length) return;

            cache.batch({
              update: cache => {
                for (const { documentId, questionIds } of pairs) {
                  cache.modify({
                    fields: {
                      aiQuestionIdsQueueing: refs => {
                        return uniqBy(ref => ref, [...refs, ...questionIds]);
                      },
                    },
                    id: cache.identify({
                      __typename: documentTypename,
                      id: documentId,
                    }),
                  });
                }
              },
            });
          },
        });

        if (result.error) {
          /**
           * `errorPolicy` is 'all', so GraphQL errors (e.g. the AI token limit
           * being reached) resolve here rather than throwing — surface them
           * instead of falling through to the "nothing to run" message below.
           */
          enqueueSnackbar('Failed to run Aline AI. Please try again or contact support.', {
            variant: 'error',
          });
          return result;
        }

        const queuedCount = result.data?.startReportExtraction?.queuedCount ?? 0;
        if (queuedCount > 0) {
          enqueueSnackbar(
            `Started Aline AI on ${queuedCount} ${queuedCount === 1 ? 'document' : 'documents'}.`,
            { variant: 'success' }
          );
        } else {
          enqueueSnackbar('No documents to run. Answers are already up to date.', {
            variant: 'info',
          });
        }
        return result;
      } catch {
        // Network/transport failure — surface it instead of leaving it silent.
        enqueueSnackbar('Failed to run Aline AI. Please try again or contact support.', {
          variant: 'error',
        });
        return { data: null };
      }
    },
    [enqueueSnackbar, startReportExtraction]
  );

  return {
    loading,
    startReportExtraction: safeStartReportExtraction,
  };
}

export function useGetQuestionGenerationJobs(documentId: UUID) {
  const { data } = Apollo.useGetQuestionGenerationJobsQuery({
    variables: { documentId },
  });
  return { questionGenerationJobs: data?.questionGenerationJobs ?? [] };
}

export function useGetInactiveExtractionQuestions(documentId: UUID) {
  const { extractionQuestions, loading } = useGetExtractionQuestionsForDocument(documentId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  return { inactiveQuestions: extractionQuestions?.filter(q => !q.isActive) ?? [], loading };
}

export function useStartExtractionQuestionGenerationJob() {
  const [startGenerationJob, { loading }] = Apollo.useStartQuestionGenerationJobMutation();

  const safeStartExtractionQuestionGenerationJob = useCallback(
    (options: Parameters<Apollo.StartQuestionGenerationJobMutationHookResult[0]>[0]) => {
      return startGenerationJob({
        ...options,
        update: (cache, { data }) => {
          cache.modify({
            fields: {
              questionGenerationJobs: (refs = []) => {
                const ref = cache.writeFragment({
                  data: data?.startQuestionGenerationJob,
                  fragment: gql`
                    fragment NewExtractionQuestionGenerationJob on ExtractionQuestionGenerationJob {
                      __typename
                      id
                    }
                  `,
                });
                return ref ? [...refs, ref] : refs;
              },
            },
          });
        },
      });
    },
    [startGenerationJob]
  );

  return {
    loading,
    startQuestionGenerationJob: safeStartExtractionQuestionGenerationJob,
  };
}

export function useGetAllExtractionQuestions() {
  const { data } = Apollo.useGetExtractionQuestionsQuery();
  const extractionQuestions = useMemo(
    () => data?.extractionQuestions ?? [],
    [data?.extractionQuestions]
  );
  return { extractionQuestions };
}

export function useSetQuestionInputsOptimistic(questionId: UUID) {
  const [setQuestionInputs, { loading }] = Apollo.useSetQuestionInputsMutation();
  const setQuestionInputsOptimistic = useCallback(
    async (options: Parameters<Apollo.SetQuestionInputsMutationHookResult[0]>[0]) => {
      return setQuestionInputs({
        ...options,
        optimisticResponse: ({ input }) => {
          return {
            __typename: 'Mutation',
            setQuestionInputs: {
              __typename: 'ExtractionQuestion',
              // This will get replaced by server data once the question is updated...
              contentHash: uuidv4(),
              id: input.questionId,
              inputFileFields: input.inputFileFields ?? [],
              inputs: input.inputQuestionIds.map(id => ({
                __typename: 'ExtractionQuestion',
                id,
              })),
            },
          };
        },
        refetchQueries: [
          {
            query: Apollo.GetExtractionQuestionWithDependenciesDocument,
            variables: {
              id: questionId,
            } satisfies Apollo.GetExtractionQuestionWithDependenciesQueryVariables,
          },
        ],
      });
    },
    [setQuestionInputs, questionId]
  );

  return { loading, setQuestionInputsOptimistic };
}

export function useAddQuestionOptionsOptimistic(questionId: UUID) {
  const [addQuestionOptions, { loading }] = Apollo.useAddQuestionOptionsMutation();
  const addQuestionOptionsOptimistic = useCallback(
    (options: Parameters<Apollo.AddQuestionOptionsMutationHookResult[0]>[0]) => {
      return addQuestionOptions({
        ...options,
        optimisticResponse: ({ input }) => {
          return {
            __typename: 'Mutation',
            addQuestionOptions: input.options.map(option => ({
              __typename: 'QuestionOption',
              color: option.color,
              id: uuidv4(),
              label: option.label,
              order: option.order,
            })),
          };
        },
        refetchQueries: [
          {
            query: Apollo.GetExtractionQuestionWithDependenciesDocument,
            variables: {
              id: questionId,
            } satisfies Apollo.GetExtractionQuestionWithDependenciesQueryVariables,
          },
        ],
        update: (cache, { data }) => {
          if (!data?.addQuestionOptions) return;

          cache.modify({
            fields: {
              options: (existingOptionRefs = []) => {
                const newRefs = data.addQuestionOptions.flatMap(option => {
                  const newRef = cache.writeFragment({
                    data: option,
                    fragment: gql`
                      fragment NewQuestionOption on QuestionOption {
                        __typename
                        id
                      }
                    `,
                  });
                  return newRef ? [newRef] : [];
                });
                return [...existingOptionRefs, ...newRefs];
              },
            },
            id: cache.identify({
              __typename: extractionQuestionTypename,
              id: questionId,
            }),
          });
        },
      });
    },
    [addQuestionOptions, questionId]
  );

  return { addQuestionOptionsOptimistic, loading };
}

export function useDeleteQuestionOptionOptimistic(questionId: UUID) {
  const [deleteQuestionOption, { loading }] = Apollo.useDeleteQuestionOptionMutation();
  const deleteQuestionOptionOptimistic = useCallback(
    (options: Parameters<Apollo.DeleteQuestionOptionMutationHookResult[0]>[0]) => {
      return deleteQuestionOption({
        ...options,
        optimisticResponse: ({ optionId }) => {
          return {
            __typename: 'Mutation',
            deleteQuestionOption: optionId,
          };
        },
        refetchQueries: [
          {
            query: Apollo.GetExtractionQuestionWithDependenciesDocument,
            variables: {
              id: questionId,
            } satisfies Apollo.GetExtractionQuestionWithDependenciesQueryVariables,
          },
        ],
        update: (cache, { data }) => {
          if (!data?.deleteQuestionOption) return;

          cache.modify({
            fields: {
              options: (existingOptionRefs = [], { readField }) => {
                return (existingOptionRefs as Reference[]).filter(
                  (ref: Reference) => readField('id', ref) !== data.deleteQuestionOption
                );
              },
            },
            id: cache.identify({
              __typename: extractionQuestionTypename,
              id: questionId,
            }),
          });
        },
      });
    },
    [deleteQuestionOption, questionId]
  );

  return { deleteQuestionOptionOptimistic, loading };
}
